import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useError } from '../../hooks/useError';
import ErrorState from '../../components/ui/ErrorState';

const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRetrying, setIsRetrying] = useState(false);
  const { errorInfo, clearError, retryLastRequest } = useError();

  // Extract retry information from localStorage and location state
  const [storedErrorContext, setStoredErrorContext] = useState<{
    errorType?: 'network' | 'server' | 'timeout' | 'bad-gateway';
    previousUrl?: string;
    retryUrl?: string;
    message?: string;
    statusCode?: number;
    timestamp?: number;
    requestInfo?: {
      url?: string;
      method?: string;
      baseURL?: string;
    };
  } | null>(null);

  useEffect(() => {
    // Try to get error context from localStorage
    try {
      const stored = localStorage.getItem('error_context');
      if (stored) {
        const parsed = JSON.parse(stored);
        setStoredErrorContext(parsed);
        // Clear the stored context after reading
        localStorage.removeItem('error_context');
      }
    } catch (error) {
      console.error('Failed to parse stored error context:', error);
    }
  }, []);

  const locationState = location.state as {
    retryAction?: () => Promise<void>;
    retryUrl?: string;
    previousUrl?: string;
    errorType?: 'network' | 'server' | 'timeout' | 'bad-gateway';
    message?: string;
    statusCode?: number;
  } | null;

  // Determine values from multiple sources (priority: errorInfo > storedErrorContext > locationState)
  const retryAction = errorInfo?.retryAction || locationState?.retryAction;
  const retryUrl = errorInfo?.retryUrl || storedErrorContext?.retryUrl || storedErrorContext?.previousUrl || locationState?.retryUrl;
  const previousUrl = errorInfo?.previousUrl || storedErrorContext?.previousUrl || locationState?.previousUrl;
  const errorType = errorInfo?.errorType || storedErrorContext?.errorType || locationState?.errorType || 'server';
  const statusCode = errorInfo?.statusCode ?? storedErrorContext?.statusCode ?? locationState?.statusCode;

  const handleTryAgain = async () => {
    if (isRetrying) {
      return;
    }

    setIsRetrying(true);
    
    try {
      if (retryAction) {
        // If we have a specific retry action, execute it
        console.log('🔄 ErrorPage - Executing retry action');
        await retryAction();
        
        // Clear error context after successful retry
        clearError();
        
        // After successful retry, navigate back to previous page or fallback
        const targetUrl = previousUrl || retryUrl || '/self-appraisal/okr-submission';
        navigate(targetUrl, { replace: true });
      } else if (errorInfo && retryLastRequest) {
        // Use error context retry method
        console.log('🔄 ErrorPage - Using error context retry');
        await retryLastRequest();

        clearError();

        // Navigate back to previous page or fallback
        const targetUrl = previousUrl || retryUrl || '/self-appraisal/okr-submission';
        navigate(targetUrl, { replace: true });
      } else if (retryUrl) {
        // If we have a retry URL, navigate to it
        console.log('🔄 ErrorPage - Navigating to retry URL:', retryUrl);
        navigate(retryUrl, { replace: true });
      } else if (previousUrl) {
        // Navigate back to the previous page that caused the error
        console.log('🔄 ErrorPage - Navigating back to previous URL:', previousUrl);
        navigate(previousUrl, { replace: true });
      } else {
        // Fallback: try to go back in history or navigate to default route
        console.log('🔄 ErrorPage - Using fallback navigation');
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate('/self-appraisal/okr-submission', { replace: true });
        }
      }
    } catch (error) {
      console.error('❌ ErrorPage - Retry failed:', error);
      // If retry fails, just navigate back to a safe route
      navigate('/self-appraisal/okr-submission', { replace: true });
    } finally {
      setIsRetrying(false);
    }
  };

  const deriveErrorState = () => {
    if (errorType === 'network') {
      return {
        type: 'network-error' as const,
        title: 'Connection Problem',
        defaultMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
      };
    }

    if (errorType === 'timeout' || statusCode === 504) {
      return {
        type: 'timeout' as const,
        title: 'Request Timeout',
        defaultMessage: 'The server took too long to respond. Please try again in a few moments.',
      };
    }

    if (errorType === 'bad-gateway' || statusCode === 502) {
      return {
        type: 'bad-gateway' as const,
        title: 'Bad Gateway',
        defaultMessage: 'The server encountered a temporary error. Please try again shortly.',
      };
    }

    return {
      type: 'server-error' as const,
      title: 'Oops! Something went wrong',
      defaultMessage: 'The server is temporarily unable to service your request. Please try again in a few moments.',
    };
  };

  const derivedError = deriveErrorState();
  const displayMessage = derivedError.defaultMessage;
  const canRetry = Boolean(retryAction || retryUrl || previousUrl || errorInfo || storedErrorContext);

  return (
    <main className="min-h-screen bg-white flex items-center justify-center px-4 py-8" role="main">
      <div className="w-full max-w-3xl">
        <ErrorState
          type={derivedError.type}
          title={derivedError.title}
          message={statusCode ? `${displayMessage} (Status ${statusCode})` : displayMessage}
          onRetry={canRetry ? handleTryAgain : undefined}
          retryLabel={isRetrying ? 'Retrying...' : 'Try Again'}
          showRetryButton={canRetry}
        />
      </div>
    </main>
  );
};

export default ErrorPage;
